import { MdsSharedByVariant } from "@/design-system/components/item-list/types";
import { AccountProfileImage } from "@/components/layout/components/account-profile/AccountProfileImage";
import { asFirstName } from "@/domains/string/name";
import { observer } from "mobx-react-lite";
import { ACCOUNT_PROFILE_DISPLAY_NAME_FALLBACK } from "@/domains/common/constants";
import { ProfileKind, ProfileSize } from "@/components/layout/components/account-profile";
import styled from "@emotion/styled";
import { ContactObservable } from "@/store/contacts/ContactObservable";
import { MDSRowItemSlotName } from "@/mds";
import { mdsPanelWidthsValue } from "@/design-system/foundations/breakpoints";

export interface MdsItemListItemSharedByBoxProps {
  sharedBy?: ContactObservable;
  sharedByVariant?: MdsSharedByVariant;
  slot?: MDSRowItemSlotName;
}

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const MdsItemListItemSharedByBox = observer(
  ({
    sharedBy,
    sharedByVariant = MdsSharedByVariant.SharedBy,
  }: MdsItemListItemSharedByBoxProps) => {
    const displayName = sharedBy?.profileDisplayName ?? ACCOUNT_PROFILE_DISPLAY_NAME_FALLBACK;

    const sharingFirstNameText =
      sharedByVariant === "SHARED_BY"
        ? `${asFirstName(displayName)} shared`
        : `${asFirstName(displayName)} added`;

    const sharingFullText =
      sharedByVariant === "SHARED_BY" ? `Shared by ${displayName}` : `Added by ${displayName}`;

    if (!sharedBy) {
      return null;
    }

    return (
      <Wrapper>
        <Container title={sharingFullText}>
          <AccountProfileImage
            size={ProfileSize.Small}
            profile={{
              kind: ProfileKind.Contact,
              data: sharedBy,
            }}
          />

          <Text>{sharingFirstNameText}</Text>
        </Container>
      </Wrapper>
    );
  }
);

const Wrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-start;
  flex-shrink: 1;
  overflow: hidden;
`;

const Container = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: #696e7d;
  display: flex;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  border-radius: 4px;
  gap: 8px;
`;

const Text = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  @container mds-row-item (max-width: ${mdsPanelWidthsValue.headerBreakpoint}px) {
    display: none;
    width: 0;
  }
`;
